import type { ProductReview, Review } from '~~/shared/globalTypes/reviews';

type SharedMediaData = {
  id: string;
  reviewId: number;
  url: string;
  caption: string;
};

type ParsedPhoto = SharedMediaData & { type: 'photo' };
type ParsedVideo = SharedMediaData & { type: 'video'; thumbnailUrl: string };

type ParsedReviewMedia = ParsedPhoto | ParsedVideo;

export type ReviewMedia = {
  totalPhotos: number | null;
  reviews: ProductReview['reviewsWithPhotos']['reviews'];
  photos: ParsedReviewMedia[];
};

// Only meant to be used as a callback for reduce operations
const parseReviewsToMedia =
  (reviewVideoUploadEnabled: boolean = false) =>
  (acc: ParsedReviewMedia[], review: Review): ParsedReviewMedia[] => {
    const photos = review.hasPhotos ? review.photos : [];
    const videos = (reviewVideoUploadEnabled && review.videos) || [];
    const { reviewId } = review;
    const currentPhotos: ParsedPhoto[] = photos.map((x) => ({
      reviewId,
      id: x.photoId,
      url: x?.sizes?.normal?.url,
      caption: x.caption || '',
      type: 'photo',
    }));
    const currentVideos: ParsedVideo[] = videos.map((x) => ({
      reviewId,
      id: x.videoId,
      url: x.url,
      thumbnailUrl: x.thumbnailUrl,
      caption: x.caption || '',
      type: 'video',
    }));
    return [...acc, ...currentPhotos, ...currentVideos];
  };

export default function parseReviewsMedia(reviewData: ProductReview | null, reviewVideoUploadEnabled: boolean = false): ReviewMedia {
  const { reviewsWithPhotos } = reviewData ?? {};
  const totalPhotos = reviewsWithPhotos?.totalPhotos ?? null;
  const reviews = reviewsWithPhotos?.reviews ?? [];

  return {
    totalPhotos,
    reviews,
    photos: reviews.reduce(parseReviewsToMedia(reviewVideoUploadEnabled), []),
  };
}
